import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  styled,
  Typography,
  Box,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Queue as QueueIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

import MainContainer from '../../../components/MainContainer';
import MainHeader from '../../../components/MainHeader';
import Title from '../../../components/Title';
import MainHeaderButtonsWrapper from '../../../components/MainHeaderButtonsWrapper';
import TableRowSkeleton from '../../../components/TableRowSkeleton';

// Services
import queuesService from '../../../services/queuesService';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1),
  overflowY: 'auto',
  maxHeight: 'calc(100vh - 200px)'
}));

const SearchField = styled(TextField)(({ theme }) => ({
  marginRight: theme.spacing(2),
  minWidth: 300
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'active':
        return theme.palette.success.main;
      case 'inactive':
        return theme.palette.error.main;
      case 'paused':
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return {
    backgroundColor: getStatusColor(),
    color: theme.palette.common.white,
    fontWeight: 'bold'
  };
});

// Constantes
const QUEUE_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PAUSED: 'paused'
};

const QUEUE_COLORS = [
  '#2196f3', '#4caf50', '#ff9800', '#9c27b0', '#f44336',
  '#00bcd4', '#795548', '#607d8b', '#e91e63', '#3f51b5'
];

const Queues = () => {
  // Estados
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState('');
  const [queues, setQueues] = useState([]);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingQueue, setEditingQueue] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: QUEUE_COLORS[0],
    greetingMessage: '',
    status: QUEUE_STATUS.ACTIVE
  });

  const showNotification = useCallback((message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  }, []);

  // Função para buscar filas da API
  const fetchQueues = useCallback(async () => {
    try {
      setLoading(true);
      
      const response = await queuesService.getQueues();
      let queuesList = response.data || response;
      
      // Filtrar baseado na busca se houver parâmetro de busca
      if (searchParam) {
        queuesList = queuesList.filter(queue => 
          queue.name.toLowerCase().includes(searchParam.toLowerCase())
        );
      }
      
      setQueues(queuesList);
      
    } catch (error) {
      showNotification('Erro ao carregar filas', 'error');
      setQueues([]);
    } finally {
      setLoading(false);
    }
  }, [searchParam, showNotification]);

  // Carregar filas ao montar o componente
  useEffect(() => {
    fetchQueues();
  }, [fetchQueues]);

  // Handler para busca
  const handleSearch = useCallback((event) => {
    setSearchParam(event.target.value);
  }, []);

  // Abrir modal para nova fila
  const handleNewQueue = useCallback(() => {
    setEditingQueue(null);
    setFormData({
      name: '',
      color: QUEUE_COLORS[0],
      greetingMessage: '',
      status: QUEUE_STATUS.ACTIVE
    });
    setModalOpen(true);
  }, []);

  // Abrir modal para editar fila
  const handleEditQueue = useCallback((queue) => {
    setEditingQueue(queue);
    setFormData({
      name: queue.name,
      color: queue.color,
      greetingMessage: queue.greetingMessage || '',
      status: queue.status || QUEUE_STATUS.ACTIVE
    });
    setModalOpen(true);
  }, []);

  // Salvar fila (criar ou editar)
  const handleSaveQueue = useCallback(async () => {
    try {
      if (!formData.name.trim()) {
        showNotification('Nome da fila é obrigatório', 'error');
        return;
      }

      setLoading(true);
      
      if (editingQueue) {
        await queuesService.updateQueue(editingQueue.id, formData);
        showNotification('Fila atualizada com sucesso!', 'success');
      } else {
        await queuesService.createQueue(formData);
        showNotification('Fila criada com sucesso!', 'success');
      }
      
      setModalOpen(false);
      fetchQueues();
      
    } catch (error) {
      console.error('Erro ao salvar fila:', error);
      showNotification('Erro ao salvar fila', 'error');
    } finally {
      setLoading(false);
    }
  }, [formData, editingQueue, fetchQueues, showNotification]);

  // Deletar fila
  const handleDeleteQueue = useCallback(async (queueId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta fila?')) {
      return;
    }

    try {
      setLoading(true);
      
      await queuesService.deleteQueue(queueId);
      
      showNotification('Fila excluída com sucesso!', 'success');
      fetchQueues();
      
    } catch (error) {
      console.error('Erro ao excluir fila:', error);
      showNotification('Erro ao excluir fila', 'error');
    } finally {
      setLoading(false);
    }
  }, [fetchQueues, showNotification]);

  // Alternar status da fila
  const handleToggleStatus = useCallback(async (queueId, currentStatus) => {
    const newStatus = currentStatus === QUEUE_STATUS.ACTIVE 
      ? QUEUE_STATUS.INACTIVE 
      : QUEUE_STATUS.ACTIVE;

    try {
      await queuesService.updateQueue(queueId, { status: newStatus });
      
      setQueues(prev => prev.map(queue => 
        queue.id === queueId 
          ? { ...queue, status: newStatus }
          : queue
      ));
      
      showNotification(
        `Fila ${newStatus === QUEUE_STATUS.ACTIVE ? 'ativada' : 'desativada'} com sucesso!`, 
        'success'
      );
      
    } catch (error) {
      console.error('Erro ao alterar status da fila:', error);
      showNotification('Erro ao alterar status da fila', 'error');
    }
  }, [showNotification]);

  // Obter label do status
  const getStatusLabel = (status) => {
    switch (status) {
      case QUEUE_STATUS.ACTIVE:
        return 'Ativa';
      case QUEUE_STATUS.INACTIVE:
        return 'Inativa';
      case QUEUE_STATUS.PAUSED:
        return 'Pausada';
      default:
        return status;
    }
  };

  // Obter ícone do status
  const getStatusIcon = (status) => {
    switch (status) {
      case QUEUE_STATUS.ACTIVE:
        return <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />;
      case QUEUE_STATUS.INACTIVE:
        return <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />;
      case QUEUE_STATUS.PAUSED:
        return <SettingsIcon sx={{ fontSize: 16, color: 'warning.main' }} />;
      default:
        return null;
    }
  };

  // Normalizar status da fila (compatibilidade com diferentes formatos)
  const normalizeStatus = (queue) => {
    if (queue.active === true) return QUEUE_STATUS.ACTIVE;
    if (queue.active === false) return QUEUE_STATUS.INACTIVE;
    return queue.status || QUEUE_STATUS.INACTIVE;
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Filas de Atendimento</Title>
        <MainHeaderButtonsWrapper>
          <SearchField
            placeholder="Buscar filas..."
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'grey.500' }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNewQueue}
          >
            Nova Fila
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <StyledPaper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell align="center">Cor</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Tickets</TableCell>
              <TableCell align="center">Usuários</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableRowSkeleton columns={6} />}
            {queues.map((queue) => {
              const status = normalizeStatus(queue);
              return (
                <TableRow key={queue.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <QueueIcon sx={{ mr: 1, color: queue.color || '#2196f3' }} />
                      <Typography variant="body2">
                        {queue.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: queue.color || '#2196f3',
                        margin: '0 auto'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" justifyContent="center">
                      {getStatusIcon(status)}
                      <StatusChip
                        label={getStatusLabel(status)}
                        status={status}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {queue.ticketsCount || queue.tickets?.count || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" justifyContent="center">
                      <PersonIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      <Typography variant="body2">
                        {queue.usersCount || queue.users?.count || 0}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={status === QUEUE_STATUS.ACTIVE}
                          onChange={() => handleToggleStatus(queue.id, status)}
                          size="small"
                        />
                      }
                      label=""
                      sx={{ mr: 1 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleEditQueue(queue)}
                      title="Editar fila"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteQueue(queue.id)}
                      title="Excluir fila"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        
        {/* Estado vazio */}
        {!loading && queues.length === 0 && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={8}
          >
            <QueueIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {searchParam ? 'Nenhuma fila encontrada' : 'Nenhuma fila cadastrada'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {searchParam 
                ? 'Tente ajustar os termos da busca'
                : 'Clique em "Nova Fila" para adicionar uma fila de atendimento'
              }
            </Typography>
          </Box>
        )}
      </StyledPaper>

      {/* Modal de Criação/Edição */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingQueue ? 'Editar Fila' : 'Nova Fila'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Nome da Fila"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              margin="normal"
              required
            />
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Cor</InputLabel>
              <Select
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                label="Cor"
              >
                {QUEUE_COLORS.map((color, index) => (
                  <MenuItem key={color} value={color}>
                    <Box display="flex" alignItems="center">
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: color,
                          mr: 1
                        }}
                      />
                      {color}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                label="Status"
              >
                <MenuItem value={QUEUE_STATUS.ACTIVE}>Ativa</MenuItem>
                <MenuItem value={QUEUE_STATUS.INACTIVE}>Inativa</MenuItem>
                <MenuItem value={QUEUE_STATUS.PAUSED}>Pausada</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Mensagem de Saudação"
              value={formData.greetingMessage}
              onChange={(e) => setFormData(prev => ({ ...prev, greetingMessage: e.target.value }))}
              margin="normal"
              multiline
              rows={3}
              placeholder="Digite a mensagem que será enviada quando um usuário iniciar uma conversa nesta fila..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button 
            onClick={handleSaveQueue} 
            variant="contained" 
            disabled={loading}
          >
            {editingQueue ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notificações */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </MainContainer>
  );
};

export default Queues;
